import { Activity, Wifi, WifiOff, Eye, EyeOff, Clock, AlertTriangle, Network } from "lucide-react";
import { CauseBadge, humanSecs } from "./offlineCauses";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const Stat = ({ icon: Icon, label, value, warn, testid }) => (
  <div className="rounded-lg bg-slate-50 dark:bg-slate-900 px-3 py-2 min-w-0" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</p>
    <p className={`text-sm font-semibold truncate ${warn ? "text-amber-600" : "text-slate-900 dark:text-white"}`}>{value}</p>
  </div>
);

export const DiagnosticsCard = ({ data }) => {
  const d = data.diag || {};
  const ev = data.events_24h || {};
  const fails = (ev.hb_fail_net || 0) + (ev.hb_fail_srv || 0);
  const uptime = d.boot_at ? humanSecs((Date.now() - new Date(d.boot_at)) / 1000) : "—";
  if (!d.reported_at) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-800 p-4 text-xs text-slate-500" data-testid="kiosk-diag-empty">
        <p className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-1.5"><Activity className="h-4 w-4 text-indigo-600" /> Diagnostics</p>
        <p className="mt-1">Waiting for the tablet to send diagnostics. This starts automatically once the kiosk page is reloaded with the latest version.</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 space-y-3" data-testid="kiosk-diag-card">
      <p className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-1.5"><Activity className="h-4 w-4 text-indigo-600" /> Diagnostics <span className="text-[11px] font-normal text-slate-400">· reported {fmt(d.reported_at)}</span></p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat testid="kiosk-diag-network" icon={d.online === false ? WifiOff : Wifi} label="Tablet network" value={d.online === false ? "No connection" : `Connected${d.connection ? ` · ${d.connection}` : ""}`} warn={d.online === false} />
        <Stat testid="kiosk-diag-screen" icon={d.visibility === "hidden" ? EyeOff : Eye} label="Kiosk screen" value={d.visibility === "hidden" ? "Hidden / locked" : "Showing"} warn={d.visibility === "hidden"} />
        <Stat testid="kiosk-diag-uptime" icon={Clock} label="Page running for" value={uptime} />
        <Stat testid="kiosk-diag-fails" icon={AlertTriangle} label="Failed check-ins (24h)" value={`${fails}${ev.js_error ? ` · ${ev.js_error} error(s)` : ""}`} warn={fails > 0 || ev.js_error > 0} />
      </div>
      <div className="text-[11px] text-slate-500 space-y-0.5">
        <p>Last page start: <b className="text-slate-700 dark:text-slate-300">{fmt(d.boot_at)}</b>{ev.boot > 1 ? ` · ${ev.boot} restarts in 24h` : ""}</p>
        <p className="flex items-center gap-1"><Network className="h-3 w-3" /> {data.last_ip_change ? <>Network changed {fmt(data.last_ip_change.t)} <span className="text-slate-400">({data.last_ip_change.detail})</span></> : "No network changes in the last 24h"}</p>
        {(ev.net_off || ev.hidden) ? <p>Last 24h: {ev.net_off || 0} Wi-Fi drop(s) · {ev.hidden || 0} time(s) hidden/locked</p> : null}
      </div>
    </div>
  );
};

export const OutageList = ({ outages, ongoing }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Why it went offline — last 14 days</p>
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden" data-testid="kiosk-outage-list">
      {ongoing && (
        <div className="px-3 py-2.5 flex flex-wrap items-center gap-2" data-testid="kiosk-outage-ongoing">
          <CauseBadge cause="unknown" />
          <span className="text-sm text-slate-700 dark:text-slate-200">Offline now · {humanSecs(ongoing)}</span>
          <span className="text-[11px] text-slate-400">The cause is worked out when the tablet reconnects.</span>
        </div>
      )}
      {outages.map((o, i) => (
        <div key={o.id} className="px-3 py-2.5 space-y-1" data-testid={`kiosk-outage-${i}`}>
          <div className="flex flex-wrap items-center gap-2">
            <CauseBadge cause={o.cause} testid={`kiosk-outage-cause-${i}`} />
            <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{humanSecs(o.duration_seconds)}</span>
            <span className="text-[11px] text-slate-400">{fmt(o.start)} → {fmt(o.end)}</span>
            {o.fleet_wide && <span className="text-[10px] font-bold uppercase tracking-wide text-fuchsia-600">Several kiosks</span>}
          </div>
          {o.evidence?.length > 0 && <p className="text-[11px] text-slate-500">{o.evidence.join(" · ")}</p>}
        </div>
      ))}
      {!outages.length && !ongoing && <p className="px-3 py-4 text-xs text-slate-400">No offline periods with diagnostics yet. New outages are explained here automatically.</p>}
    </div>
  </div>
);
